import { queryClient } from "@/lib/query-client";
import { rootLoader } from "@/lib/root-loader";
import { UserProvider } from "@/providers";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import { Toaster } from "sonner";

import { App } from "./App.tsx";
import { AssetsView } from "./components/assets-view";
import { DashboardView } from "./components/dashboard-view";
import { DownloadHistoryView } from "./components/download-history-view";
import { ErrorBoundary } from "./components/error-boundary";
import { FileShareView } from "./components/file-share-view";
import { JFrogTokensView } from "./components/jfrog-tokens-view";
import { LandingPage } from "./components/landing-page";
import { LicensesView } from "./components/licenses-view";
import { NotFound } from "./components/not-found";
import { SecurityStatementsView } from "./components/security-statements-view";
import { DataTable as UserDataTable } from "./components/users-table.tsx";
import { VersionsView } from "./components/versions-view";
import { ZitadelTokensView } from "./components/zitadel-tokens-view";
import { oidcConfig } from "./constants/auth";
import "./index.css";
import { getEnv } from "./lib/get_env";
import { initializeOTel } from "./lib/otel-instrumentation.ts";
import { initWebVitals } from "./lib/otel-web-vitals.ts";

const otelEnabled = getEnv("VITE_OTEL_ENABLED") === "true";

if (otelEnabled) {
  initializeOTel({
    enabled: otelEnabled,
    endpoint: getEnv("VITE_OTEL_ENDPOINT") || "http://localhost:4318",
    serviceName: "customer-portal-ui",
    environment: getEnv("VITE_ENVIRONMENT") || "development",
    backendUrl: getEnv("VITE_BACKEND_SERVICE_URL") || "http://localhost:8000",
    samplingRate: getEnv("VITE_OTEL_SAMPLING_RATE") ? parseFloat(getEnv("VITE_OTEL_SAMPLING_RATE")!) : 1.0,
    debugMode: getEnv("VITE_OTEL_DEBUG") === "true",
  });
}

initWebVitals();
const router = createBrowserRouter([
  {
    path: "/welcome",
    element: <LandingPage />,
  },
  {
    path: "/",
    loader: rootLoader,
    element: (
      <UserProvider>
        <App />
      </UserProvider>
    ),
    children: [
      {
        path: "/users",
        element: <UserDataTable />,
      },
      {
        path: "/",
        index: true,
        element: <DashboardView />,
      },
      {
        path: "/assets",
        element: <AssetsView />,
      },
      {
        path: "/versions",
        element: <VersionsView />,
      },
      {
        path: "/security-statements",
        element: <SecurityStatementsView />,
      },
      {
        path: "/licenses",
        element: <LicensesView />,
      },
      {
        path: "/organization-tokens",
        element: <ZitadelTokensView />,
      },
      {
        path: "/jfrog-tokens",
        element: <JFrogTokensView />,
      },
      {
        path: "/downloads",
        element: <DownloadHistoryView />,
      },
      {
        path: "/file-share",
        element: <FileShareView />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider {...oidcConfig}>
          <RouterProvider router={router} />
          <Toaster position="top-right" duration={3000} richColors />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
